import { useContext, useState, useEffect, useRef } from "react";
import "./HomeAssistente.scss";

import Sidebar from "../../../shared/components/sidebar/Sidebar";
import Navbar from "../../../shared/components/navbar/Navbar";

import MarcacoesAssistente from "./components/marcacoesAssistente/MarcacoesAssistente";
import DashboardIcon from "@mui/icons-material/Dashboard";
import { AuthContext } from "../../../shared/context/auth-context";
import { ClinicaContext } from "../../../shared/context/clinica-context";
import { useHttpClient } from "../../../shared/hooks/http-hook";
import Tasks from "../components/tasks/Tasks";
import PdfListaAtendimentos from "../../../shared/components/PdfGenerator/PdfListaAtendimentos/PdfListaAtendimentos";

import { useWebSocketSubscription } from "../../../shared/context/websocket-context";

import "../components/top5tratamentos/Top5tratamentos.scss";
import "../../../shared/css/Checkbox.scss";
import StockAlerts from "../components/stockDashboard/StockAlerts";
import StockMovements from "../components/stockDashboard/StockMovements";
import MarcacoesTratamentoAssistente from "./components/marcacoesAssistente/MarcacoesTratamentoAssistente";

const DAILY_MESSAGES = [
  "Que hoje o motor funcione, o paciente colabore e o café nunca acabe!",
  "Sorriso no rosto, luvas nas mãos e fé que o paciente não desmaie!",
  "Hoje o plano é simples: anestesia pega, raio-x não falha e a impressora coopera!",
  "Que teus pacientes sejam pontuais e teus instrumentos, fiéis!",
  "Se o motor travar, respira fundo: o humor não pode travar junto!",
  "Mais um dia para provar que curar também é arte!",
  "Hoje o bisturi brilha e o stress fica na autoclave!",
  "Que o único sangramento do dia seja o da gengiva — e o mínimo possível!",
  "Hoje é dia de salvar sorrisos e evitar dramas!",
  "Lembra: cada paciente é único — e quase todos dizem 'não vai doer, né?'",
  "Que tua agenda flua melhor que resina recém-polida!",
  "Hoje você é o herói mascarado da saúde bucal!",
  "Nada como começar o dia com mãos firmes e paciência renovada!",
  "Que o som do motor te inspire, não te assuste!",
  "Que teu humor dure mais que o efeito da anestesia!",
  "Lembra: um sorriso bem feito vale mais que mil planilhas!",
  "Hoje é dia de brilhar — sem esquecer o protetor facial!",
  "Que teus atendimentos sejam leves e as consultas, certeiras!",
  "Se o dia apertar, lembra: até o dente do siso nasce com dificuldade!",
  "Sorrir é terapêutico — e tu receitas isso todo dia!",
  "Que teu foco seja tão preciso quanto tua lâmina!",
  "Hoje o diagnóstico vem fácil e o paciente entende na primeira!",
  "Lembra: o verdadeiro milagre é o paciente dizer 'não doeu nada'!",
  "Que o teu consultório seja mais zen que sala de yoga!",
  "Hoje a meta é simples: menos urgências, mais gratidão!",
  "Que tuas mãos curem, teus olhos percebam e teu humor contagie!",
  "Nada como um bom atendimento para restaurar dentes e ânimos!",
  "Que o motor gire, o plano aprove e o sorriso apareça!",
  "Hoje é dia de espalhar saúde — e um pouquinho de luz pelo consultório!",
  "Lembra: um bom profissional não perde o foco... nem o fio dental!",
  "Que teu dia seja leve, produtivo e cheio de sorrisos sinceros — inclusive o teu!",
];

function HomeAssistente() {
  const { sendRequest } = useHttpClient();
  const auth = useContext(AuthContext);
  const clinica = useContext(ClinicaContext);
  const [listaPresenca, setListaPresenca] = useState([]);

  const [dadosPdf, setDadosPdf] = useState({
    show: false,
    listaAtendimentoData: [],
    medico: "",
  });

  const notificationPresencaSoundRef = useRef(null);
  const lastListaPresencaLengthRef = useRef(0);
  const lastPresencaEventIdRef = useRef(null);

  useEffect(() => {
    // Create the audio element for notification sound
    const audio = new Audio("/sounds/presenca.wav");
    audio.volume = 1; // Set volume to 50%
    notificationPresencaSoundRef.current = audio;

    return () => {
      // Clean up if needed
      audio.pause();
      audio.src = "";
    };
  }, []);

  useWebSocketSubscription(
    async (parsedSocketData) => {
      if (
        parsedSocketData.tabela === "listapresencas" &&
        parsedSocketData.eventId !== lastPresencaEventIdRef.current
      ) {
        lastPresencaEventIdRef.current = parsedSocketData.eventId;

        const fetchListaPresenca = async () => {
          try {
            const responseData = await sendRequest(
              `${process.env.REACT_APP_BACKEND_LINK}/api/listapresenca/${clinica.clinica._id}`,
              "GET",
              null,
              {
                Authorization: "Bearer " + auth.token,
              }
            );

            if (
              responseData.listaPresenca.length > listaPresenca.length &&
              responseData.listaPresenca.length >
                lastListaPresencaLengthRef.current
            ) {
              lastListaPresencaLengthRef.current += 1;
            } else {
              lastListaPresencaLengthRef.current -= 1;
            }

            setListaPresenca(responseData.listaPresenca);
          } catch (err) {}
        };

        fetchListaPresenca();
      }
    },
    [auth, clinica.clinica._id, sendRequest],
    "Dashboard"
  );

  useEffect(() => {
    const fetchListaPresenca = async () => {
      try {
        const responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/listapresenca/${clinica.clinica._id}`,
          "GET",
          null,
          {
            Authorization: "Bearer " + auth.token,
          }
        );
        setListaPresenca(responseData.listaPresenca);
        lastListaPresencaLengthRef.current = responseData.listaPresenca.length;
      } catch (err) {}
    };
    fetchListaPresenca();
  }, [sendRequest, auth.userId, clinica, auth.token]);

  const showSinglePage = () => {
    setDadosPdf({ show: false });
  };

  const verPdf = (medico, allMarcacoes, dataLista, incluirObservacoes) => {
    const filteredMarcacoes = [];
    allMarcacoes.forEach((mr) => {
      if (mr.medico._id === medico.id) {
        filteredMarcacoes.push({
          cliente: mr.nome,
          contacto: mr.contacto,
          procedimento: mr.avaliacao
            ? "Avaliação"
            : mr.tratamentoIsolado
            ? mr.tratamento
            : clinica.clinica.configuracoes.configuracoes.tipoAgendamento
                .tipo === "Tratamento"
            ? mr.tratamento.designacao
            : mr.categoriaTratamento.categoria,
          data_inicio_execucao: mr.data_inicio_execucao,
          data_fim_execucao: mr.data_fim_execucao,
          observacoesAgendamento: mr.observacoesAgendamento,
        });
      }
    });

    setDadosPdf({
      show: true,
      listaAtendimentoData: filteredMarcacoes,
      medico: medico.nome,
      dataLista,
      incluirObservacoes: incluirObservacoes,
    });
  };

  ///////////////////////////////////////////////////
  return (
    <>
      {dadosPdf.show && (
        <PdfListaAtendimentos
          showPreviousPage={showSinglePage}
          medico={dadosPdf.medico}
          clinica={clinica.clinica.nome}
          listaAtendimentoData={dadosPdf.listaAtendimentoData}
          dataLista={dadosPdf.dataLista}
          incluirObservacoes={dadosPdf.incluirObservacoes}
        />
      )}

      <div className="home">
        <Sidebar />
        <div className="homeContainer">
          <Navbar title="Dashboard" icon={DashboardIcon} />
          <div className="dashboard__secretaria__heading">
            <div className="dashboard__ola">
              <span className="dashboard__ola__big">{`Olá, ${auth.nomeAbreviado}!`}</span>
              <span className="dashboard__ola__small">
                {DAILY_MESSAGES[new Date().getDate() - 1] || ""}
              </span>
            </div>
          </div>

          <div className="dashboard-administrativo__marcacoes caixa">
            {clinica.clinica.configuracoes.configuracoes.tipoAgendamento
              .tipo === "Tratamento" ? (
              <MarcacoesTratamentoAssistente
                verPdf={verPdf}
                listaPresencaProp={listaPresenca}
              />
            ) : (
              <MarcacoesAssistente
                verPdf={verPdf}
                listaPresencaProp={listaPresenca}
              />
            )}
          </div>
          <div className="dashboard-assistente__stock__container">
            <StockAlerts
              clinicaId={clinica.clinica._id}
              sendRequest={sendRequest}
              auth={auth}
            />
            <StockMovements
              clinicaId={clinica.clinica._id}
              sendRequest={sendRequest}
              auth={auth}
              titulo="Minhas últimas movimentações"
            />
          </div>

          <div className="dashboard__bottom caixa tasks__container">
            <Tasks sendRequest={sendRequest} clinica={clinica} auth={auth} />
          </div>
        </div>
      </div>
    </>
  );
}

export default HomeAssistente;
